import { useEffect, useRef, useState, useMemo } from 'react';
import Select from 'react-select';
import Switch from 'react-switch';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { ReactSVG } from 'react-svg';

import { env } from '../../config';
import { formatDate } from '../../utils';
import { getBasePlaylists, savePlaylist } from '../Screen/utils';
import PreviewModal from '../Screen/PreviewModal';
import { getSubLocationsByLocationId, getSublocationsByPlaylistId, createRule, updateRule, deleteRule } from './utils';
import BrandingConfiguration from './BrandingConfiguration';
import JourneyPlaylistConfiguration from './JourneyPlaylistConfiguration';
import JourneyInsight from './JourneyInsight';
import { getLocations } from '../Locations/utils';
import dropdownOptions from '../Screen/dropdownOptions.json';
import journeyPlaylistFeatures from './journeyPlaylistFeatures.json';

import plusIcon from '../../images/icons/screens/white-plus.svg';
import copyIcon from '../../images/icons/screens/copy.svg';
import gearIcon from '../../images/icons/screens/gear.svg';
import paintbrushIcon from '../../images/icons/screens/paintbrush.svg';
import playlistIcon from '../../images/icons/screens/playlist.svg';
import mapMarkerIcon from '../../images/icons/screens/map-marker.svg';
import openReportIcon from '../../images/icons/analytics/open-report.svg';
import eyeIcon from '../../images/icons/screens/eye.svg';
import returnIcon from '../../images/icons/screens/return.svg';
import phoneTouchIcon from '../../images/icons/screens/interactivity.svg';
import chartUpwardIcon from '../../images/icons/chart-upward.svg';
import insightIcon from '../../images/icons/insight.svg';
import saveIcon from '../../images/icons/screens/save.svg';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const RULE_TYPES = ['modes', 'agencies', 'parkingCost', 'subsidy', 'name', 'finalLegIntegrationUrl'];

const RULE_DIRECTIONS = [
    { value: '', label: 'Both (internal & external)' },
    { value: 'internal', label: 'Internal (departing)' },
    { value: 'external', label: 'External (arriving)' },
];

const ALL_MODES = ['walk', 'bus', 'subway', 'train', 'tram', 'ferry', 'bike', 'bikeshare', 'car', 'rideHailing', 'scooter'];
const ALL_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const RULE_INTERVALS = ['', 'hourly', 'daily', 'weekly', 'monthly'];

const EMPTY_RULE = {
    type: 'modes',
    direction: null,
    config: { value: [] },
    interval: null,
    relevantModes: null,
    isActive: true,
    timeFrom: null,
    timeTo: null,
    days: null,
    dateFrom: null,
    dateTo: null,
};

const selectStyles = {
    container: (base, state) => ({ ...base, flex: 1 }),
    control: (base, state) => ({
        ...base,
        border: 'none',
        boxShadow: state.isDisabled ? 'unset' : '0px 2px 8px 4px rgba(0,0,0,0.09)',
        borderRadius: '8px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    option: (base, state) => ({
        ...base,
        cursor: 'pointer',
        color: state.isDisabled ? '#919191' : 'inherit',
    }),
};

const icons = {
    eyeIcon,
    gearIcon,
    insightIcon,
    paintbrushIcon,
    phoneTouchIcon,
    playlistIcon,
    returnIcon,
    saveIcon
};

const MAPS_LIBRARIES = ['places'];

const JourneyPlaylistPage = ({ userData, customer, handleContentModalOpen, permissions }) => {
    const [playlist, setPlaylist] = useState(null);
    const [savedPlaylist, setSavedPlaylist] = useState(null);
    const [sublocations, setSublocations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [stateChanged, setStateChanged] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ message: null, error: false });
    const [journeyPlaylists, setJourneyPlaylists] = useState([]);
    const [showNewPlaylistModal, setShowNewPlaylistModal] = useState(false);
    const [showOpenPlaylistModal, setShowOpenPlaylistModal] = useState(false);
    const [showUndoConfirmModal, setShowUndoConfirmModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState('settings');
    const [editingSublocation, setEditingSublocation] = useState(null);
    const openedLocationIdRef = useRef(null);

    const { isLoaded: mapLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: env.GMAPS_KEY || '',
        libraries: MAPS_LIBRARIES,
    });

    const getBasePlaylistsWrapper = () => {
        getBasePlaylists(
            customer.id,
            customer.id === userData.customer_id && userData.role !== 'af_admin' ? userData.sub : null,
            'journey'
        ).then(data => {
            setJourneyPlaylists(data);
        });
    }

    useEffect(() => {
        getBasePlaylistsWrapper();

        getLocations(customer.id).then(data => {
            if (Array.isArray(data)) {
                let sortedLocations = data.sort((a, b) => a.name.localeCompare(b.name));
                setLocations(sortedLocations);
            }
        });
    }, []);

    useEffect(() => {
        const openedFor = openedLocationIdRef.current;
        openedLocationIdRef.current = null;

        if (!playlist?.locationId) {
            setSublocations([]);
            return;
        }
        // Skip: getSublocationsByPlaylistId already fetched sublocations for this location on Open.
        if (openedFor === playlist.locationId) return;

        getSubLocationsByLocationId(playlist.locationId).then(apiSubs => {
            if (!Array.isArray(apiSubs)) return;
            setSublocations(apiSubs);

            const freshSubs = apiSubs.map(s => ({ sublocationId: s.id, name: s.name, isActive: true }));
            setPlaylist(prev => {
                const existingSubs = prev?.journeyConfigs?.sublocations || [];
                const freshSubs = apiSubs.map(s => {
                    const existing = existingSubs.find(c => c.sublocationId === s.id);
                    return {
                        sublocationId: s.id,
                        name: existing?.name ?? s.name,
                        isActive: existing?.isActive ?? true,
                    };
                });
                const journeyConfigs = { ...(prev?.journeyConfigs || {}) };
                if (freshSubs.length > 0) {
                    journeyConfigs.sublocations = freshSubs;
                } else {
                    delete journeyConfigs.sublocations;
                }
                return { ...prev, journeyConfigs };
            });
        });
    }, [playlist?.locationId]);

    // Merge API sublocation data (lat/lng, isFinal) with journeyConfigs (name, isActive)
    const configSubs = playlist?.journeyConfigs?.sublocations || [];
    let letterIdx = 0;
    const subsWithBadge = [...sublocations]
        .sort((a, b) => {
            if (a.isFinal !== b.isFinal) return a.isFinal ? -1 : 1;
            return new Date(a.createdAt) - new Date(b.createdAt);
        })
        .map(apiSub => {
            const cfg = configSubs.find(s => s.sublocationId === apiSub.id);
            const nameRule = (apiSub.rules || []).find(r =>
                r.type === 'name' && r.isActive && r.sublocationId === apiSub.id
            );
            return {
                ...apiSub,
                name: nameRule?.config?.value || cfg?.name || apiSub.name,
                isActive: cfg?.isActive ?? true,
                badge: apiSub.isFinal ? 'F' : LETTERS[letterIdx++],
            };
        });

    const DC = { lat: 38.9072, lng: -77.0369 };
    const mappable = sublocations.filter(s => isFinite(parseFloat(s.latitude)) && isFinite(parseFloat(s.longitude)));
    const mapCenter = mappable.length > 0
        ? {
            lat: mappable.reduce((s, sub) => s + parseFloat(sub.latitude), 0) / mappable.length,
            lng: mappable.reduce((s, sub) => s + parseFloat(sub.longitude), 0) / mappable.length,
        }
        : DC;

    const handlePlaylistChange = (field, value) => {
        setPlaylist(prev => ({ ...prev, [field]: value }));
        setStateChanged(true);
    };

    const handleBrandingChange = (field, value) => {
        setPlaylist(prev => ({
            ...prev,
            brandingConfigs: { ...(prev?.brandingConfigs || {}), [field]: value }
        }));
        setStateChanged(true);
    };

    const handleSublocationSave = ({ sublocationId, name, isActive }) => {
        const updated = configSubs.map(s =>
            s.sublocationId === sublocationId ? { ...s, name, isActive } : s
        );
        setPlaylist(prev => ({
            ...prev,
            journeyConfigs: { ...(prev?.journeyConfigs || {}), sublocations: updated }
        }));
        setStateChanged(true);
    };

    const handleRulesSave = (sublocationId, rules) => {
        setSublocations(prev => prev.map(s => s.id === sublocationId ? { ...s, rules } : s));
    };

    const handleSave = async () => {
        const resp = await savePlaylist({ playlist }, customer);
        if (resp?.status === 200) {
            setStatusMessage({ message: 'Your updates have been saved.', error: false });
            setSavedPlaylist(playlist);
            setStateChanged(false);
            getBasePlaylistsWrapper();
        } else {
            setStatusMessage({ message: 'Something went wrong. Please try again.', error: true });
        }
    };

    const undoChange = () => {
        setPlaylist(savedPlaylist);
        setStateChanged(false);
    };

    // const isEditable = useMemo(() => {
    //     let retval = false;
    //     if ((playlist?.id && playlist?.location?.id in permissions['journey'] && permissions['journey'][playlist.location.id].includes('editor')) ||
    //         playlist?._new) {
    //         retval = true;
    //     }

    //     return retval;
    // }, [playlist]);

    // TODO: The above is the better permission. However for testing right now with this product only editable 
    // within the company, a more lenient method is needed
    const isEditable = userData.role === 'af_admin';

    const fillSvgColor = (svg, stroke, fill) => {
        if (stroke || fill) {
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', stroke);
                svg.children[i].setAttribute('fill', fill);
            }
        }
    }

    const userHasAccess = (feature) => {
        const defaultAllowed = ['basicConfiguration'].includes(feature.key);
        let journeyAllowed = 'journey' in permissions && playlist?.location?.id in permissions['journey'];
        let viewableFeature = feature.key in permissions && (playlist?.location?.id in permissions[feature.key]);

        return defaultAllowed || (viewableFeature && journeyAllowed);
    }

    const modifyIconColor = (svg, feature) => {
        const needsFill = ['interactivityIcon', 'insightIcon', 'paintbrushIcon', 'phoneTouchIcon'];
        if (!userHasAccess(feature) || !feature.enabled || !playlist?.createdAt) {
            // Gray if no access
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', '#CCD2D3');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', '#CCD2D3');
                }
            }
        } else if (selectedTab == feature.key) {
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', 'white');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', 'white');
                }
            }
        } else if (userHasAccess(feature) && feature.enabled && playlist?.createdAt) {
            // Explicitly turn it the "active" color
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', '#373739');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', '#373739');
                }
            }
        }
    }

    const getMenuItem = (feature) => {
        if (feature.endGrouping) {
            menu.push(<div className="tab-separator" key={feature.key}></div>);
            return;
        } else {
            return <div key={feature.key} title={feature.title}>
                    <div
                        className={`icon-container ${!userHasAccess(feature) || (!playlist?.id && !playlist?._new) || !feature.enabled ? 'disabled' : (selectedTab === feature.key && (playlist?.id || playlist?._new) ? 'active' : '')}`}
                        key={feature.key + '-reactsvg'}
                        onClick={() => setSelectedTab(feature.key)}
                        title={feature.title}
                    >
                        <ReactSVG className="icon feature-icon" src={icons[feature.icon]} beforeInjection={(svg) => modifyIconColor(svg, feature)}/>
                    </div>
                </div>
        }
    }

    let menu = [];
    journeyPlaylistFeatures.forEach((feature) => {
        menu.push(getMenuItem(feature));
    })

    const distributionUrl = `${env.LOADER_URL}/${playlist?.shortCode || ''}`;
    const locationOptions = locations.map(l => ({ label: l.name, value: l.id }));

    return (
        <div className="JourneyPlaylistPage">
            {statusMessage.message && (
                <StatusModal setStatusMessage={setStatusMessage} statusMessage={statusMessage} />
            )}
            {showOpenPlaylistModal && (
                <OpenPlaylistModal
                    playlists={journeyPlaylists}
                    selectedPlaylist={playlist}
                    onOpen={async pl => {
                        const apiSubs = await getSublocationsByPlaylistId(pl.id);
                        if (Array.isArray(apiSubs)) setSublocations(apiSubs);

                        openedLocationIdRef.current = pl.locationId;
                        setPlaylist(pl);
                        setSavedPlaylist(pl);
                        setStateChanged(false);
                        setShowOpenPlaylistModal(false);
                    }}
                    onClose={() => setShowOpenPlaylistModal(false)}
                />
            )}
            {showNewPlaylistModal && (
                <NewPlaylistModal
                    locations={locations}
                    customer={customer}
                    onCreate={newPlaylist => {
                        setPlaylist(newPlaylist);
                        setSavedPlaylist(newPlaylist);
                        setStateChanged(true);
                        setShowNewPlaylistModal(false);
                    }}
                    onClose={() => setShowNewPlaylistModal(false)}
                />
            )}
            {showPreviewModal && (
                <PreviewModal state={{ playlist }} setShowPreviewModal={setShowPreviewModal} />
            )}
            {showUndoConfirmModal && (
                <UndoConfirmModal
                    onConfirm={() => { undoChange(); setShowUndoConfirmModal(false); }}
                    onClose={() => setShowUndoConfirmModal(false)}
                />
            )}
            {editingSublocation && (
                <SublocationEditModal
                    sublocation={editingSublocation}
                    rules={editingSublocation.rules || []}
                    playlistId={playlist?.id}
                    onSaveDetails={handleSublocationSave}
                    onSaveRules={handleRulesSave}
                    onClose={() => setEditingSublocation(null)}
                />
            )}

            <div className="actions-tab">
                <div className="left-container">
                    <button className="action-btn" onClick={() => setShowNewPlaylistModal(true)}>
                        <img src={plusIcon} alt="add" className="tab-icon" />
                    </button>
                    <button className="action-btn" onClick={() => setShowOpenPlaylistModal(true)}>
                        <img src={openReportIcon} alt="open" className="tab-icon" />
                    </button>
                    {playlist && (
                        <div className="playlist-info">
                            <div className="playlist-name">{playlist.label}</div>
                            {playlist.location && (
                                <div className="playlist-address">
                                    {[playlist.location.address1, playlist.location.city, playlist.location.state].filter(Boolean).join(', ')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="right-container">
                    {menu}
                    <div className="tab-separator" />
                    <div title="Preview">
                        <div
                            className={`icon-container${!playlist || stateChanged ? ' disabled' : ''}`}
                            title="Preview"
                            onClick={() => playlist && !stateChanged && setShowPreviewModal(true)}
                        >
                            <ReactSVG
                                alt="preview"
                                beforeInjection={(svg) => !playlist?.id || stateChanged ? fillSvgColor(svg, '#CCD2D3', '#CCD2D3') : null}
                                className="icon feature-icon"
                                src={icons['eyeIcon']}
                            />
                        </div>
                    </div>
                    <div title="Undo">
                        <div
                            className={`icon-container${!playlist || !isEditable ? ' disabled' : ''}`}
                            title="Undo"
                            onClick={() => isEditable ? playlist && stateChanged && setShowUndoConfirmModal(true) : null}
                        >
                            <ReactSVG
                                alt="undo"
                                beforeInjection={(svg) => !playlist?.id || !isEditable ? fillSvgColor(svg, '#CCD2D3', '#ffffff') : null}
                                className="icon feature-icon"
                                src={icons['returnIcon']}
                            />
                        </div>
                    </div>
                    <div title="Save">
                        <div
                            className={`icon-container save-btn${!playlist || !isEditable ? ' disabled' : (stateChanged ? ' changed' : '')}`}
                            title="Save"
                            onClick={() => isEditable ? playlist && handleSave() : null}
                        >
                            <ReactSVG
                                alt="save"
                                beforeInjection={(svg) => !playlist?.id || !isEditable ? fillSvgColor(svg, '#CCD2D3', '#CCD2D3') : null}
                                className={`icon feature-icon ${stateChanged ? 'invert-color' : ''}`}
                                src={icons['saveIcon']}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {selectedTab === 'journeyBrandingConfiguration' ? (
                <BrandingConfiguration
                    playlist={playlist}
                    onChange={handleBrandingChange}
                    handleContentModalOpen={handleContentModalOpen}
                    permissions={permissions}
                />
            ) : selectedTab === 'journeyPlaylistConfiguration' ? (
                <JourneyPlaylistConfiguration
                    playlist={playlist}
                    onChange={(field, value) => { handlePlaylistChange(field, value); }}
                    customer={customer}
                    permissions={permissions}
                />
            ) : selectedTab === 'journeyInsight' ? (
                <JourneyInsight
                    playlist={playlist}
                />
            ) : (
            <div className="journey-content">
                <div className="left-panel">
                  <div className="config-section">
                    <section className="config-subsection">
                        <h2>Distribution</h2>
                        <div className="distribution-row">
                            <span className="dist-label">Active:</span>
                            <Switch
                                checked={false}
                                onChange={() => {}}
                                onColor="#2386EE"
                                offColor="#CCD2D3"
                                height={22}
                                width={44}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                disabled
                            />
                            <input
                                className="url-input disabled"
                                type="text"
                                readOnly
                                value={distributionUrl}
                                disabled
                            />
                        </div>
                    </section>

                    <section className="config-subsection">
                        <h2>Localization settings</h2>
                        <p className="section-description">
                            Ensure your content is formatted properly by choosing the primary device(s) where you plan to display it.
                        </p>
                        <div className="form-row">
                            <span className="form-label">Name:</span>
                            <input
                                className="form-input"
                                type="text"
                                value={playlist?.label || ''}
                                onChange={e => handlePlaylistChange('label', e.target.value)}
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="form-row">
                            <span className="form-label">Location:</span>
                            <Select
                                className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                options={locationOptions}
                                value={locationOptions.find(o => o.value === playlist?.locationId) || null}
                                onChange={opt => handlePlaylistChange('locationId', opt.value)}
                                styles={selectStyles}
                                isDisabled={!isEditable}
                            />
                        </div>
                        <div className="form-row">
                            <span className="form-label">Language:</span>
                            <Select
                                className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                options={dropdownOptions.LANGUAGE_OPTIONS}
                                value={
                                    dropdownOptions.LANGUAGE_OPTIONS.find(o => o.value === playlist?.language)
                                    || dropdownOptions.LANGUAGE_OPTIONS[0]
                                }
                                onChange={opt => handlePlaylistChange('language', opt.value)}
                                styles={selectStyles}
                                isDisabled={!isEditable}
                            />
                        </div>
                        <div className="form-row">
                            <span className="form-label">Measurements:</span>
                            <Select
                                className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                options={dropdownOptions.MEASUREMENTS}
                                value={
                                    dropdownOptions.MEASUREMENTS.find(o => o.value === playlist?.measurement)
                                    || dropdownOptions.MEASUREMENTS[0]
                                }
                                onChange={opt => handlePlaylistChange('measurement', opt.value)}
                                styles={selectStyles}
                                isDisabled={!isEditable}
                            />
                        </div>
                        <p className="support-note">
                            Need to add, remove, or update a location? Contact{' '}
                            <a href="mailto:support@actionfigure.ai">support@actionfigure.ai</a>
                        </p>
                    </section>

                    <section className="config-subsection">
                        <h2>Journey Hubs</h2>
                        <p className="section-description">
                            Journey Hubs are how you control where and how people get to the final destination. These could be parking lots, transit stations, and more.
                        </p>
                        <div className="hubs-table">
                            <div className="hubs-header">
                                <span className="col-hub">Hub</span>
                                <span className="col-active">Active</span>
                                <span className="col-label">Label</span>
                            </div>
                            {subsWithBadge.map(sub => (
                                <div className="hub-row" key={sub.id} onClick={() => {
                                    const apiSub = sublocations.find(s => s.id === sub.id) || sub;
                                    setEditingSublocation({ ...apiSub, name: sub.name, isActive: sub.isActive, badge: sub.badge });
                                }}>
                                    <div className="col-hub">
                                        <div className={`hub-badge${sub.isFinal ? ' final' : ''}`}>
                                            {sub.badge}
                                        </div>
                                    </div>
                                    <div className="col-active">
                                        <span className={`active-indicator${sub.isActive ? ' active' : ''}`}>
                                            {sub.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="col-label">{sub.name}</div>
                                </div>
                            ))}
                        </div>
                        <p className="support-note">
                            Need to add, remove, or update a hub? Contact{' '}
                            <a href="mailto:support@actionfigure.ai">support@actionfigure.ai</a>
                        </p>
                    </section>
                  </div>
                </div>

                <div className="right-panel">
                    {mapLoaded ? (
                        <GoogleMap
                            mapContainerClassName="journey-map"
                            center={mapCenter}
                            zoom={13}
                            options={{ disableDefaultUI: true, zoomControl: true }}
                        >
                            {subsWithBadge.map(sub => {
                                const lat = parseFloat(sub.latitude);
                                const lng = parseFloat(sub.longitude);
                                return isFinite(lat) && isFinite(lng) ? (
                                    <OverlayView
                                        key={sub.id}
                                        position={{ lat, lng }}
                                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                        getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
                                    >
                                        <div className={`hub-map-marker${sub.isFinal ? ' final' : ''}`}>
                                            {sub.badge}
                                        </div>
                                    </OverlayView>
                                ) : null;
                            })}
                        </GoogleMap>
                    ) : (
                        <div className="map-loading">Loading map...</div>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

const OpenPlaylistModal = ({ playlists, selectedPlaylist, onOpen, onClose }) => {
    const [highlighted, setHighlighted] = useState(selectedPlaylist);
    const [sortColumn, setSortColumn] = useState('updatedAt');
    const [sortDirection, setSortDirection] = useState('desc');

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const handleSort = (col) => {
        if (sortColumn === col) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(col);
            setSortDirection('asc');
        }
    };

    const sorted = [...playlists].sort((a, b) => {
        let aVal = sortColumn === 'locationId' ? (a.location?.name || '') : (a[sortColumn] ?? '');
        let bVal = sortColumn === 'locationId' ? (b.location?.name || '') : (b[sortColumn] ?? '');
        if (['updatedAt', 'createdAt'].includes(sortColumn)) {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
        }
        const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : (aVal > bVal ? 1 : aVal < bVal ? -1 : 0);
        return sortDirection === 'asc' ? cmp : -cmp;
    });

    return (
        <div className="modal-wrapper">
            <div className="OpenPlaylistModal modal">
                <div className="modal-header">
                    <h2>Open Journey Playlist</h2>
                </div>
                <div className="table-container">
                    <table className="playlists-table">
                        <tbody>
                            <tr className="header-row">
                                <th className="table-header" onClick={() => handleSort('shortCode')}>Shortcode</th>
                                <th className="table-header" onClick={() => handleSort('label')}>Name</th>
                                <th className="table-header" onClick={() => handleSort('locationId')}>Location</th>
                                <th className="table-header" onClick={() => handleSort('updatedBy')}>Updated by</th>
                                <th className="table-header" onClick={() => handleSort('updatedAt')}>Updated</th>
                            </tr>
                            {sorted.map(pl => (
                                <tr
                                    key={pl.id}
                                    className={`playlist-row${pl.id === highlighted?.id ? ' selected' : ''}`}
                                    onClick={() => setHighlighted(pl)}
                                >
                                    <td>{pl.shortCode}</td>
                                    <td>{pl.label}</td>
                                    <td>{pl.location?.name || '—'}</td>
                                    <td>{pl.updatedBy || '—'}</td>
                                    <td>{formatDate(pl.updatedAt)}</td>
                                </tr>
                            ))}
                            {playlists.length === 0 && (
                                <tr><td colSpan={3} className="empty-row">No journey playlists found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button
                        className="button modal-button"
                        disabled={!highlighted}
                        onClick={() => highlighted && onOpen(highlighted)}
                    >
                        Open
                    </button>
                </div>
            </div>
        </div>
    );
};

const NewPlaylistModal = ({ locations, customer, onCreate, onClose }) => {
    const [name, setName] = useState('');
    const [locationId, setLocationId] = useState(null);

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const locationOptions = locations.map(l => ({ label: l.name, value: l.id }));

    const handleCreate = () => {
        onCreate({
            customerId: customer.id,
            locationId,
            label: name,
            language: 'en',
            screenType: 'journey',
            deviceType: 'landscape',
            access: 'public',
            type: 'base',
            createdAt: new Date(),
            _new: true,
        });
    };

    return (
        <div className="modal-wrapper">
            <div className="NewPlaylistModal modal">
                <div className="modal-header">
                    <h2>Create a new Journey Playlist</h2>
                </div>
                <div className="modal-form">
                    <div className="modal-form-row">
                        <label className="modal-form-label">Name:</label>
                        <input
                            className="modal-form-input"
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. PGA Championship at Aronomink"
                        />
                    </div>
                    <div className="modal-form-row">
                        <label className="modal-form-label">Location:</label>
                        <Select
                            className="modal-form-select"
                            options={locationOptions}
                            value={locationOptions.find(o => o.value === locationId) || null}
                            onChange={opt => setLocationId(opt.value)}
                            styles={selectStyles}
                            placeholder="Select a location..."
                        />
                    </div>
                </div>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button
                        className="button modal-button"
                        disabled={!name || !locationId}
                        onClick={handleCreate}
                    >
                        Create
                    </button>
                </div>
            </div>
        </div>
    );
};

const StatusModal = ({ setStatusMessage, statusMessage }) => {
    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    return (
        <div className="modal-wrapper">
            <div className="StatusModal modal">
                <p className={statusMessage.error ? 'error' : ''}>{statusMessage.message}</p>
                <div className="bottom-container">
                    <button
                        className="button modal-button"
                        onClick={() => setStatusMessage({ message: null, error: false })}
                    >
                        Ok
                    </button>
                </div>
            </div>
        </div>
    );
};

const UndoConfirmModal = ({ onConfirm, onClose }) => {
    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    return (
        <div className="modal-wrapper">
            <div className="StatusModal modal">
                <p>This will remove all changes since the last time you saved. Are you sure?</p>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button className="button modal-button" onClick={onConfirm}>Ok</button>
                </div>
            </div>
        </div>
    );
};

const SublocationEditModal = ({ sublocation, rules, playlistId, onSaveDetails, onSaveRules, onClose }) => {
    const [isActive, setIsActive] = useState(sublocation.isActive);
    const [draftRules, setDraftRules] = useState(rules);
    const backdropRef = useRef(null);

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) onClose();
    };

    const handleSave = () => {
        onSaveDetails({ sublocationId: sublocation.id, name: sublocation.name, isActive });
        onSaveRules(sublocation.id, draftRules);
        onClose();
    };

    return (
        <div className="modal-wrapper" ref={backdropRef} onClick={handleBackdropClick}>
            <div className="SublocationEditModal modal">
                <div className="modal-header">
                    <h2>Edit Sublocation {sublocation.badge}</h2>
                </div>
                <div className="modal-form">
                    <div className="modal-form-row">
                        <label className="modal-form-label">Name:</label>
                        <p className="modal-form-static">{sublocation.originalName}</p>
                    </div>
                    <div className="modal-form-hint">Current name: {sublocation.name}</div>
                    <div className="modal-form-row">
                        <label className="modal-form-label">Active:</label>
                        <Switch
                            checked={!!isActive}
                            onChange={setIsActive}
                            onColor="#2386EE"
                            offColor="#CCD2D3"
                            height={22}
                            width={44}
                            uncheckedIcon={false}
                            checkedIcon={false}
                        />
                    </div>
                </div>

                <SublocationRulesEditor
                    rules={draftRules}
                    onChange={setDraftRules}
                    sublocationId={sublocation.id}
                    playlistId={playlistId}
                />

                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button className="button modal-button" onClick={handleSave}>Save</button>
                </div>
            </div>
        </div>
    );
};

const SublocationRulesEditor = ({ rules, onChange, sublocationId, playlistId }) => {
    const [editRule, setEditRule] = useState(null);
    const [isNewRule, setIsNewRule] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleAddRule = () => {
        setEditRule({ ...EMPTY_RULE, sublocationId });
        setIsNewRule(true);
    };

    const handleEditRule = (rule) => {
        setEditRule({ ...rule });
        setIsNewRule(false);
    };

    const handleCancelEdit = () => {
        setEditRule(null);
        setIsNewRule(false);
    };

    const handleToggleActive = (rule) => {
        onChange(rules.map(r => r.id === rule.id ? { ...r, isActive: !r.isActive } : r));
    };

    const handleDeleteRule = async (id) => {
        try {
            await deleteRule(id);
            onChange(rules.filter(r => r.id !== id));
            if (editRule?.id === id) setEditRule(null);
        } catch (e) {
            console.error('Failed to delete rule', e);
        }
    };

    const handleSaveRule = async () => {
        setLoading(true);
        try {
            if (isNewRule) {
                const created = await createRule({ ...editRule, sublocationId, playlistId });
                if (created?.id) {
                    onChange([...rules, created]);
                    setEditRule(null);
                    setIsNewRule(false);
                }
            } else {
                await updateRule(editRule);
                onChange(rules.map(r => r.id === editRule.id ? editRule : r));
                setEditRule(null);
            }
        } catch (e) {
            console.error('Failed to save rule', e);
        }
        setLoading(false);
    };

    const handleRuleFieldChange = (field, value) => {
        setEditRule(prev => ({ ...prev, [field]: value }));
    };

    const handleConfigValueChange = (value) => {
        setEditRule(prev => ({ ...prev, config: { ...prev.config, value } }));
    };

    const handleModeToggle = (mode) => {
        const current = Array.isArray(editRule.config?.value) ? editRule.config.value : [];
        const updated = current.includes(mode) ? current.filter(m => m !== mode) : [...current, mode];
        handleConfigValueChange(updated);
    };

    const handleDayToggle = (day) => {
        const current = editRule.days || [];
        const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
        handleRuleFieldChange('days', updated.length ? updated : null);
    };

    const handleRelevantModeToggle = (mode) => {
        const current = editRule.relevantModes || [];
        const updated = current.includes(mode) ? current.filter(m => m !== mode) : [...current, mode];
        handleRuleFieldChange('relevantModes', updated.length ? updated : null);
    };

    const formatConfigValue = (rule) => {
        if (!rule.config?.value && rule.config?.value !== 0) return '—';
        if (Array.isArray(rule.config.value)) return rule.config.value.join(', ') || '—';
        if (rule.type === 'finalLegIntegrationUrl') {
            const { label, value } = rule.config.value || {};
            return [label, value].filter(Boolean).join(': ') || '—';
        }
        return String(rule.config.value);
    };

    const formatSchedule = (rule) => {
        const parts = [];
        if (rule.days?.length) parts.push(rule.days.map(d => d.slice(0, 3)).join(', '));
        if (rule.timeFrom || rule.timeTo) parts.push(`${rule.timeFrom || ''}–${rule.timeTo || ''}`);
        if (rule.dateFrom || rule.dateTo) parts.push(`${rule.dateFrom || ''}→${rule.dateTo || ''}`);
        return parts.join(' | ') || '—';
    };

    return (
        <div className="sublocation-rules-container">
            <div className="rules-header">
                <h3>Rules</h3>
                <button className="add-rule-btn" onClick={handleAddRule}>+ Add Rule</button>
            </div>

            {rules.length === 0 && !isNewRule
                ? <p className="no-rules-text">No rules configured for this sublocation.</p>
                : <div className="locations-table-wrapper">
                    <table className="rules-table locations-table">
                        <thead>
                            <tr className="header-row">
                                <th className="table-header">Type</th>
                                <th className="table-header">Direction</th>
                                <th className="table-header">Value</th>
                                <th className="table-header">Schedule</th>
                                <th className="table-header">Active</th>
                                <th className="table-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            {rules.map(rule => (
                                <tr key={rule.id} className={`location-row ${editRule?.id === rule.id ? 'selected' : ''}`}>
                                    <td>{rule.type}</td>
                                    <td>{rule.direction || 'both'}</td>
                                    <td>{formatConfigValue(rule)}</td>
                                    <td>{formatSchedule(rule)}</td>
                                    <td>
                                        <Switch
                                            checked={rule.isActive}
                                            onChange={() => handleToggleActive(rule)}
                                            onColor="#2386EE"
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            height={16}
                                            width={36}
                                        />
                                    </td>
                                    <td>
                                        <button className="rule-action-btn" onClick={() => handleEditRule(rule)}>Edit</button>
                                        <button className="rule-action-btn rule-action-btn--delete" onClick={() => handleDeleteRule(rule.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }

            {editRule && (
                <SublocationRuleForm
                    rule={editRule}
                    isNew={isNewRule}
                    onFieldChange={handleRuleFieldChange}
                    onConfigValueChange={handleConfigValueChange}
                    onModeToggle={handleModeToggle}
                    onDayToggle={handleDayToggle}
                    onRelevantModeToggle={handleRelevantModeToggle}
                    onSave={handleSaveRule}
                    onCancel={handleCancelEdit}
                    loading={loading}
                />
            )}
        </div>
    );
};

const SublocationRuleForm = ({ rule, isNew, onFieldChange, onConfigValueChange, onModeToggle, onDayToggle, onRelevantModeToggle, onSave, onCancel, loading }) => {
    const isNumericType = rule.type === 'parkingCost' || rule.type === 'subsidy';

    return (
        <div className="rule-form">
            <h4>{isNew ? 'New Rule' : 'Edit Rule'}</h4>
            <div className="rule-form-body">
                <div className="rule-form-left">

                    <div className="input-container">
                        <div className="label">Type:</div>
                        <select className="input" value={rule.type} onChange={e => {
                            const newType = e.target.value;
                            const isArr = newType === 'modes' || newType === 'agencies';
                            const isUrlType = newType === 'finalLegIntegrationUrl';
                            onFieldChange('type', newType);
                            onConfigValueChange(isArr ? [] : isUrlType ? { label: '', value: '' } : '');
                        }}>
                            {RULE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="input-container">
                        <div className="label">Direction:</div>
                        <select className="input" value={rule.direction ?? ''} onChange={e => onFieldChange('direction', e.target.value || null)}>
                            {RULE_DIRECTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </div>

                    {rule.type === 'modes' && (
                        <div className="input-container input-container--column">
                            <div className="label">Modes:</div>
                            <div className="checkbox-group">
                                {ALL_MODES.map(mode => (
                                    <label key={mode} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={(rule.config?.value || []).includes(mode)}
                                            onChange={() => onModeToggle(mode)}
                                        />
                                        {mode}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {rule.type === 'agencies' && (
                        <div className="input-container">
                            <div className="label">Agencies:</div>
                            <input
                                className="input"
                                type="text"
                                placeholder="Comma-separated agency names"
                                value={Array.isArray(rule.config?.value) ? rule.config.value.join(', ') : ''}
                                onChange={e => onConfigValueChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            />
                        </div>
                    )}

                    {rule.type === 'name' && (
                        <div className="input-container">
                            <div className="label">Name:</div>
                            <input
                                className="input"
                                type="text"
                                value={rule.config?.value ?? ''}
                                onChange={e => onConfigValueChange(e.target.value)}
                            />
                        </div>
                    )}

                    {rule.type === 'finalLegIntegrationUrl' && (
                        <>
                            <div className="input-container">
                                <div className="label">Label:</div>
                                <input
                                    className="input"
                                    type="text"
                                    value={rule.config?.value?.label ?? ''}
                                    onChange={e => onConfigValueChange({ ...rule.config?.value, label: e.target.value })}
                                />
                            </div>
                            <div className="input-container">
                                <div className="label">Value (URL):</div>
                                <input
                                    className="input"
                                    type="url"
                                    placeholder="https://..."
                                    value={rule.config?.value?.value ?? ''}
                                    onChange={e => onConfigValueChange({ ...rule.config?.value, value: e.target.value })}
                                />
                            </div>
                        </>
                    )}

                    {isNumericType && (
                        <div className="input-container">
                            <div className="label">Value ($):</div>
                            <input
                                className="input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={rule.config?.value ?? ''}
                                onChange={e => onConfigValueChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
                            />
                        </div>
                    )}

                    {isNumericType && (
                        <div className="input-container">
                            <div className="label">Interval:</div>
                            <select className="input" value={rule.interval ?? ''} onChange={e => onFieldChange('interval', e.target.value || null)}>
                                {RULE_INTERVALS.map(i => <option key={i} value={i}>{i || 'None'}</option>)}
                            </select>
                        </div>
                    )}

                    {rule.type === 'subsidy' && (
                        <div className="input-container input-container--column">
                            <div className="label">Relevant Modes (leave blank for all):</div>
                            <div className="checkbox-group">
                                {ALL_MODES.map(mode => (
                                    <label key={mode} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={(rule.relevantModes || []).includes(mode)}
                                            onChange={() => onRelevantModeToggle(mode)}
                                        />
                                        {mode}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="input-container">
                        <div className="label">Active:</div>
                        <Switch
                            checked={rule.isActive}
                            onChange={val => onFieldChange('isActive', val)}
                            onColor="#2386EE"
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={20}
                            width={45}
                        />
                    </div>
                </div>

                <div className="rule-form-right">
                    <div className="input-container input-container--column">
                        <div className="label">Days (leave blank for all):</div>
                        <div className="checkbox-group">
                            {ALL_DAYS.map(day => (
                                <label key={day} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={(rule.days || []).includes(day)}
                                        onChange={() => onDayToggle(day)}
                                    />
                                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="input-container">
                        <div className="label">Time from:</div>
                        <input
                            className="input"
                            type="time"
                            value={rule.timeFrom ?? ''}
                            onChange={e => onFieldChange('timeFrom', e.target.value || null)}
                        />
                    </div>

                    <div className="input-container">
                        <div className="label">Time to:</div>
                        <input
                            className="input"
                            type="time"
                            value={rule.timeTo ?? ''}
                            onChange={e => onFieldChange('timeTo', e.target.value || null)}
                        />
                    </div>

                    <div className="input-container">
                        <div className="label">Date from:</div>
                        <input
                            className="input"
                            type="date"
                            value={rule.dateFrom ?? ''}
                            onChange={e => onFieldChange('dateFrom', e.target.value || null)}
                        />
                    </div>

                    <div className="input-container">
                        <div className="label">Date to:</div>
                        <input
                            className="input"
                            type="date"
                            value={rule.dateTo ?? ''}
                            onChange={e => onFieldChange('dateTo', e.target.value || null)}
                        />
                    </div>
                </div>
            </div>

            <div className="rule-form-actions">
                <button className="save-rule-btn" onClick={onSave} disabled={loading}>
                    {loading ? 'Saving…' : isNew ? 'Create Rule' : 'Save Rule'}
                </button>
                <button className="cancel-rule-btn" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
};

export default JourneyPlaylistPage;
